import { api } from '../api/client'
import { CategoryBars, formatBytes } from '../components/charts'
import { Card, CopyableId, ErrorBanner, Spinner, StatCard } from '../components/common'
import { Meter } from '../components/meters'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Quanto sta consumando questo tenant.
 *
 * Risponde alle due domande che di solito arrivano al fornitore per telefono:
 * «quanto mi manca al limite del piano» e «perché il mio inventario è cresciuto
 * così». La prima si legge dalle barre delle quote, la seconda dalla tabella
 * dello spazio — dove quasi sempre la riga sorprendente è quella degli eventi dei
 * job, che nessuno associa a un consumo.
 *
 * Perché lo spazio è dichiarato **stimato** in modo così esplicito: i byte per
 * tenant sono ripartiti in proporzione alle righe, perché né PostgreSQL né
 * ClickHouse sanno attribuire lo spazio a un tenant senza rileggere tutti i dati.
 * Un'approssimazione dichiarata resta utilizzabile — dice se un tenant pesa dieci
 * volte un altro — mentre un'approssimazione presentata come misura porterebbe a
 * fatturare a byte un numero che non lo regge.
 */

// Funzioni e non costanti di modulo: `t` va chiamata quando si mostra, non
// quando il file si importa.
function etichetteConteggi(): Record<string, string> {
  return {
    cis: t('Configuration item'),
    assets: t('Asset'),
    contracts: t('Contratti'),
    services: t('Business service'),
    probes: t('Sonde'),
    credentials: t('Credenziali'),
    jobs: t('Job di discovery'),
    job_events: t('Eventi dei job'),
  }
}

function etichetteTabelle(): Record<string, string> {
  return {
    configuration_item: t('Configuration item'),
    asset: t('Asset'),
    contract: t('Contratti'),
    business_service: t('Business service'),
    appliance: t('Sonde'),
    credential: t('Credenziali'),
    discovery_job: t('Job di discovery'),
    job_event: t('Eventi dei job'),
  }
}

export default function Usage() {
  const usage = useAsync(() => api.usage(), [])

  if (usage.loading && !usage.data) return <Spinner />

  const data = usage.data
  const quotas = data?.quotas

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Utilizzo')}</h1>
          <p className="page__subtitle">
            {t('Spazio occupato dai dati del tenant e capacità residua rispetto ai limiti del piano.')}
          </p>
        </div>
        <button className="secondary" onClick={() => usage.reload()}>
          {t('Aggiorna')}
        </button>
      </header>

      <ErrorBanner error={usage.error} />

      {data && (
        <>
          {/*
            Di quale tenant stiamo parlando, e con quali identificativi.

            Sembra superfluo — chi apre la pagina è dentro il proprio tenant —
            e non lo è: l'identificativo è ciò che va scritto in un ticket, in
            una chiamata all'API o in un comando di manutenzione, e finché non
            compariva da nessuna parte l'unico modo di conoscerlo era aprire il
            database. Chi apre questa pagina lo fa quasi sempre perché sta per
            chiedere qualcosa al fornitore: è il posto giusto per trovarlo.
          */}
          <Card title={t('Questo tenant')}>
            <p style={{ marginTop: 0, fontSize: 18, fontWeight: 600 }}>{data.name || '—'}</p>
            <CopyableId label={t('Identificativo')} value={data.slug} />
            <CopyableId label={t('ID interno')} value={data.tenant_id} />
            <p className="field__hint" style={{ marginBottom: 0 }}>
              {t('L’identificativo corrisponde al nome breve del tenant presente nell’URL; l’ID interno è l’identificativo univoco utilizzato dal supporto per individuare il tenant interessato.')}
            </p>
          </Card>

          <Card title={t('Limiti del piano')}>
            {/*
              Tre situazioni diverse, e prima erano una sola frase — sbagliata.
              La pagina diceva «Nessuna licenza attiva. Le discovery sono
              bloccate» a **tutti**, perché leggeva un campo che il server non
              mandava; e quella frase era falsa anche nel caso in cui la licenza
              mancava davvero, perché senza licenza si applicano i valori di
              prova, non un blocco.
            */}
            {!quotas?.licensed ? (
              <div className="banner banner--warn">
                <strong>{t('Nessuna licenza registrata.')}</strong>{' '}
                {t('Si applicano i valori di prova del piano «{piano}»: i limiti qui sotto sono quelli, e le funzioni oltre la rete — cloud, virtualizzazione, inventario degli host — restano negate. Le discovery di rete funzionano.', { piano: quotas?.plan ?? 'trial' })}
              </div>
            ) : quotas.status === 'expired' ? (
              // Chi ha una licenza scaduta ha pagato e deve rinnovare; chi non
              // ne ha mai avuta una va contattato. Stessi entitlement, due
              // conversazioni completamente diverse.
              <div className="banner banner--error">
                <strong>{t('Licenza scaduta.')}</strong>
                {quotas.expires_at && ` ${t('Era valida fino al {data}.', { data: quotas.expires_at.slice(0, 10) })}`}{' '}
                {t('Fino al rinnovo valgono i limiti di prova, che sono quelli mostrati qui sotto.')}
              </div>
            ) : (
              <p style={{ marginTop: 0 }}>
                {t('Piano')} <strong>{quotas.plan}</strong>
                {quotas.status && quotas.status !== 'active' && ` · ${quotas.status}`}
                {quotas.expires_at && ` · ${t('scade il {data}', { data: quotas.expires_at.slice(0, 10) })}`}
              </p>
            )}

            <div className="row">
              <Meter
                label={t('Configuration item')}
                used={quotas?.cis.used ?? 0}
                limit={quotas?.cis.limit ?? null}
                percent={quotas?.cis.percent ?? null}
                unlimitedNote={
                  quotas?.licensed ? t('il piano non pone un limite') : t('nessun piano assegnato')
                }
              />
              <Meter
                label={t('Sonde')}
                used={quotas?.appliances.used ?? 0}
                limit={quotas?.appliances.limit ?? null}
                percent={quotas?.appliances.percent ?? null}
                unlimitedNote={
                  quotas?.licensed ? t('il piano non pone un limite') : t('nessun piano assegnato')
                }
              />
            </div>
          </Card>

          <Card title={t("Che cosa contiene l'inventario")}>
            <div className="stats">
              {Object.entries(etichetteConteggi()).map(([key, label]) => (
                <StatCard key={key} label={label} value={data.counts[key] ?? 0} />
              ))}
            </div>
            <p className="field__hint">
              {t('Questi conteggi sono esatti. Sono anche quelli confrontati con le quote della licenza.')}
            </p>
          </Card>

          <Card title={t('Spazio occupato')}>
            <div className="stats">
              <StatCard
                label={t('Inventario')}
                value={formatBytes(data.bytes_total)}
                hint={t('CMDB, asset, contratti, diagnostica dei job')}
              />
              <StatCard
                label={t('Traffico')}
                value={
                  data.flow_store.reachable ? formatBytes(data.flow_bytes) : t('non disponibile')
                }
                hint={
                  data.flow_store.reachable
                    ? `${t('{n} righe', { n: data.flow_rows.toLocaleString(localeCorrente()) })} · ${data.flow_store.backend}`
                    : t('lo store del traffico non risponde')
                }
                tone={data.flow_store.reachable ? undefined : 'warn'}
              />
              <StatCard label={t('Totale')} value={formatBytes(data.bytes_grand_total)} />
            </div>

            {/* Il totale è la somma di due volumi diversi — la CMDB e lo store
                del traffico, che su ClickHouse è un altro processo. Dirlo evita
                che si vada a cercare un unico disco di quella dimensione. */}
            <p className="field__hint">
              {t('Inventario e traffico utilizzano volumi distinti: lo spazio totale occupato è la somma di entrambi.')}
            </p>

            {/* La domanda di questa tabella era «dove cresce», e incolonnare
                byte la rende una ricerca del numero più lungo. Con le barre la
                riga che spiega la crescita è la prima, e quasi sempre è quella
                degli eventi dei job — che nessuno associa a un consumo. */}
            <CategoryBars
              data={Object.entries(data.bytes_by_table).map(([table, bytes]) => ({
                key: table,
                label: etichetteTabelle()[table] ?? table,
                value: bytes,
              }))}
              format={formatBytes}
              emptyHint={t("Nessuno spazio attribuito: l'inventario è vuoto.")}
            />

            <p className="field__hint">
              <strong>{t('Lo spazio è una stima.')}</strong>{' '}
              {t('Viene ripartito fra i tenant in proporzione alle righe: né PostgreSQL né ClickHouse sanno attribuire i byte a un tenant senza rileggere tutti i dati, e farlo a ogni apertura di questa pagina rallenterebbe l’inventario di tutti. Serve a capire dove cresce, non a fatturare a byte — per quello ci sono i conteggi, che sono esatti.')}
            </p>
          </Card>
        </>
      )}
    </>
  )
}
